import { useEffect, useState } from "react";
import { Brain, ChevronLeft, ChevronRight, Heart, MapPin, Sparkles } from "lucide-react";
import { formatPrice, getPriceAmount, getRentTaxLabel } from "@/utils/price";
import { getPropertyLikeCount, hasLikedProperty, savePropertyLike } from "@/utils/propertyLikes";
import { evaluateAdvisorMatch, logAdvisorDecision } from "@/lib/advisorEngine";
import { hasAdvisorAccess } from "@/lib/advisorAccess";
import { getNearbyPlaces } from "@/utils/getNearbyPlaces";
import { readZoneInsightCache, writeZoneInsightCache } from "@/utils/zoneInsightCache";
import HeartBurst from "@/components/HeartBurst";

const getAdvisorToneClass = (percentage = 0) => {
  if (percentage >= 82) return "border-emerald-100 bg-emerald-50 text-emerald-800";
  if (percentage >= 68) return "border-blue-100 bg-blue-50 text-blue-800";
  if (percentage >= 52) return "border-amber-100 bg-amber-50 text-amber-800";
  return "border-red-100 bg-red-50 text-red-800";
};

const getCoordinatesFromProperty = (property) => {
  if (property?.lat && property?.lng) {
    return {
      lat: Number(property.lat),
      lng: Number(property.lng),
    };
  }

  const mapUrl = decodeURIComponent(property?.mapUrl || "");
  const match = mapUrl.match(/q=([-0-9.]+)\s*,\s*([-0-9.]+)/);

  if (!match) return null;

  return {
    lat: Number(match[1]),
    lng: Number(match[2]),
  };
};

const normalizeLocationText = (text = "") =>
  String(text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const cleanCardLocationValue = (value = "") =>
  String(value || "")
    .replace(/^colonia\s*:?\s*/i, "")
    .replace(/^calle\s*:?\s*/i, "")
    .replace(/^blvd\s*:?\s*/i, "Blvd ")
    .replace(/^av\s*:?\s*/i, "Av ")
    .replace(/\s+/g, " ")
    .trim();

const repairLocationDisplayText = (value = "") =>
  String(value || "")
    .replace(/\bCuliacn\b/gi, "Culiac\u00e1n")
    .replace(/\bCuliacan\b/gi, "Culiac\u00e1n")
    .replace(/\bCoyoacn\b/gi, "Coyoac\u00e1n")
    .replace(/\bCoyoacan\b/gi, "Coyoac\u00e1n")
    .replace(/\bMexico\b/g, "M\u00e9xico");
const getPropertyCityState = (property = {}) => {
  const explicitCity =
    property.city ||
    property.ciudad ||
    property.municipio ||
    property.alcaldia ||
    "";
  const explicitState = property.state || property.entidad || "";
  const text = normalizeLocationText(
    [
      explicitCity,
      explicitState,
      property.address,
      property.location,
      property.zona,
      property.description,
    ].filter(Boolean).join(" ")
  );

  if (text.includes("coyoacan") || text.includes("auriga")) {
    return "Coyoac\u00e1n, Ciudad de M\u00e9xico";
  }

  if (
    text.includes("ciudad de mexico") ||
    text.includes("cdmx") ||
    text.includes("mexico city")
  ) {
    return explicitCity && explicitCity !== explicitState
      ? repairLocationDisplayText(
          [explicitCity, explicitState || "Ciudad de M\u00e9xico"].filter(Boolean).join(", ")
        )
      : "Ciudad de M\u00e9xico";
  }

  if (explicitCity || explicitState) {
    return repairLocationDisplayText([explicitCity, explicitState].filter(Boolean).join(", "));
  }

  return "Culiac\u00e1n, Sinaloa";
};
export const PropertyCard = ({ property, allProperties = [], onClick, selectedCategory, showRibbon }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [isBursting, setIsBursting] = useState(false);
  const [likeCount, setLikeCount] = useState(null);
  const [index, setIndex] = useState(0);
  const [showAdvisor, setShowAdvisor] = useState(false);
  const [advisorBusiness, setAdvisorBusiness] = useState("");
  const [analyzedBusiness, setAnalyzedBusiness] = useState("");
  const [advisorZoneInsight, setAdvisorZoneInsight] = useState(null);
  const [advisorResult, setAdvisorResult] = useState(null);
  const [isAdvisorAnalyzing, setIsAdvisorAnalyzing] = useState(false);
  const [advisorError, setAdvisorError] = useState("");
  const [savedAdvisorExamples, setSavedAdvisorExamples] = useState([]);
  const [isLoadingAdvisorExamples, setIsLoadingAdvisorExamples] = useState(false);
  const ribbonText = (property.ribbon || "").toString();
  const isLongRibbon = ribbonText.length > 8;
  const promoRibbonText = (property.promoRibbon || "").toString();
  const statusText = (property.status || "").toString();
  const isRecentlyAdded = normalizeLocationText(statusText) === "recien agregado";
  const isRentalProcessStatus = normalizeLocationText(statusText).includes("proceso de renta");

  const getMediaUrl = (item) =>
    typeof item === "string" ? item : item?.src || item?.url || "";

  const images = (property.images?.length ? property.images : [property.image]).filter(Boolean);
  const priceAmount = getPriceAmount(property);
  const rentTaxLabel = getRentTaxLabel(property);
  const coordinates = getCoordinatesFromProperty(property);
  const cityState = getPropertyCityState(property);
  const neighborhoodLabel = cleanCardLocationValue(property.address);
  const streetLabel = cleanCardLocationValue(property.location);

  useEffect(() => {
    let isMounted = true;
    setIsFavorite(false);

    getPropertyLikeCount(property.id).then((count) => {
      if (isMounted) setLikeCount(count);
    });

    return () => {
      isMounted = false;
    };
  }, [property.id]);

  useEffect(() => {
    if (!showAdvisor) return undefined;

    let isMounted = true;
    setIsLoadingAdvisorExamples(true);
    setAdvisorBusiness("");
    setAdvisorError("");

    fetch(
      `/api/nearby-places?mode=examples&propertyPublicId=${encodeURIComponent(
        property.id
      )}`,
      { cache: "no-store" }
    )
      .then((response) => (response.ok ? response.json() : {}))
      .then((data) => {
        if (!isMounted) return;
        setSavedAdvisorExamples(
          Array.isArray(data.examples) ? data.examples : []
        );
      })
      .catch(() => {
        if (isMounted) setSavedAdvisorExamples([]);
      })
      .finally(() => {
        if (isMounted) setIsLoadingAdvisorExamples(false);
      });

    return () => {
      isMounted = false;
    };
  }, [property.id, showAdvisor]);

  const handleFavorite = async (event) => {
    event.stopPropagation();
    if (hasLikedProperty(property.id)) return;

    setIsBursting(false);
    window.setTimeout(() => setIsBursting(true), 0);
    window.setTimeout(() => setIsBursting(false), 800);
    setIsFavorite(true);
    window.setTimeout(() => setIsFavorite(false), 650);
    setLikeCount((count) => (Number.isFinite(count) ? count + 1 : 1));

    const result = await savePropertyLike(property);
    if (Number.isFinite(result?.count)) {
      setLikeCount(result.count);
    }
  };

  const nextImage = (event) => {
    event.stopPropagation();
    setIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (event) => {
    event.stopPropagation();
    setIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const runAdvisorAnalysis = async (event) => {
    event.stopPropagation();
    const business = advisorBusiness.trim();
    if (!business || !coordinates) return;

    setIsAdvisorAnalyzing(true);
    setAdvisorError("");
    setAdvisorZoneInsight(null);
    setAdvisorResult(null);
    setAnalyzedBusiness("");

    try {
      const cached = readZoneInsightCache(property.id, business);
      const analysis =
        cached ||
        (await getNearbyPlaces(coordinates.lat, coordinates.lng, business, {
          propertySubmissionId: property.submissionId,
          propertyPublicId: property.id,
        }));

      if (!cached) {
        writeZoneInsightCache(property.id, business, analysis);
      }

      const result = await evaluateAdvisorMatch({
        property,
        businessQuery: business,
        zoneInsight: analysis,
        allProperties,
      });

      logAdvisorDecision({
        property,
        businessQuery: business,
        zoneInsight: analysis,
        advisorResult: result,
        coordinates,
      });

      setAdvisorZoneInsight(analysis);
      setAdvisorResult(result);
      setAnalyzedBusiness(business);
    } catch (error) {
      console.log("No se pudo analizar esta card:", error);
      setAdvisorError(
        error?.code === "ZONE_CACHE_MISS"
          ? "No hay datos guardados para este giro. Google est?? pausado para evitar cargos."
          : "No se pudo analizar la zona o reglas. Intenta de nuevo."
      );
    } finally {
      setIsAdvisorAnalyzing(false);
    }
  };

  return (
    <div
      onClick={() => onClick(property)}
      data-property-id={property.id}
      data-property-title={property.title}
      aria-label={`Propiedad ${property.id}: ${property.title}`}
      className="group flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-md border border-gray-100 bg-white hover:shadow-md"
    >
      <div className="relative aspect-square w-full overflow-hidden">
        {statusText && (
          <div className="absolute left-2 top-2 z-20">
            <div
              className={`px-2 py-0.5 text-[8px] font-bold uppercase shadow-md ${
                isRecentlyAdded || isRentalProcessStatus
                  ? "rounded-sm border border-orange-200 bg-white/95 text-orange-600 shadow-sm"
                  : "rounded-full bg-orange-500 text-white"
              }`}
            >
              {statusText}
            </div>
          </div>
        )}

        {selectedCategory === "local" && promoRibbonText && (
          <div className="absolute bottom-2 left-2 right-2 z-20 rounded-sm border border-orange-200 bg-white/95 px-1.5 py-0.5 text-center shadow-sm">
            <span className="block whitespace-nowrap text-[7px] font-black uppercase leading-4 text-orange-600 sm:text-[8px]">
              {promoRibbonText}
            </span>
          </div>
        )}

        {showRibbon && property.ribbon && (
          <div className="absolute left-[-48px] top-5 z-10 w-44 -rotate-45 bg-[#F97316] py-1 text-center shadow-md">
            <span
              className={`block px-2 font-bold uppercase tracking-wide text-white ${
                isLongRibbon ? "text-[7px] sm:text-[8px]" : "text-[8px] sm:text-[9px]"
              }`}
            >
              {property.ribbon}
            </span>
          </div>
        )}

        <button
          onClick={handleFavorite}
          aria-label={isFavorite ? "Propiedad marcada como favorita" : "Marcar propiedad como favorita"}
          title={isFavorite ? "Ya marcaste esta propiedad" : "Me gusta"}
          className={`absolute right-1 top-1 z-10 flex h-6 w-6 items-center justify-center rounded-full border text-[10px] font-bold shadow-sm transition ${
            isFavorite
              ? "border-red-100 bg-white text-red-600"
              : "border-white/70 bg-white/90 text-slate-500 hover:text-red-500"
          }`}
        >
          <HeartBurst active={isBursting} />
          <Heart size={13} fill={isFavorite ? "currentColor" : "none"} />
        </button>

        {images.length > 0 ? (
          (() => {
            const current = images[index];
            const currentUrl = getMediaUrl(current);
            const currentLabel =
              typeof current === "object" && current?.label ? current.label : "";
            const isVideo = typeof current === "object" && current.type === "video";
            const forceMuted = typeof current === "object" && current.forceMuted;

            if (isVideo) {
              return (
                <video
                  src={currentUrl}
                  controls
                  muted
                  defaultMuted
                  playsInline
                  controlsList={forceMuted ? "nodownload noremoteplayback" : undefined}
                  disablePictureInPicture={forceMuted}
                  onLoadedMetadata={(event) => {
                    event.currentTarget.volume = 0;
                    event.currentTarget.muted = true;
                  }}
                  onPlay={(event) => {
                    event.currentTarget.volume = 0;
                    event.currentTarget.muted = true;
                  }}
                  onVolumeChange={(event) => {
                    event.currentTarget.volume = 0;
                    event.currentTarget.muted = true;
                  }}
                  className="h-full w-full object-cover transition group-hover:scale-105"
                />
              );
            }

            return (
              <>
                <img
                  src={currentUrl}
                  alt={property.title}
                  className="h-full w-full object-cover transition group-hover:scale-105"
                />
                {currentLabel && (
                  <div className="absolute bottom-2 left-2 right-2 z-10">
                    <span className="inline-block rounded-full bg-[#0056D2] px-2.5 py-1 text-[9px] font-bold uppercase tracking-wide text-white shadow-md">
                      {currentLabel}
                    </span>
                  </div>
                )}
              </>
            );
          })()
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-slate-100 text-[10px] font-bold uppercase tracking-wide text-slate-400">
            Sin fotos
          </div>
        )}

        {images.length > 1 && (
          <button
            onClick={prevImage}
            className="absolute left-1 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-1 text-white"
          >
            <ChevronLeft size={14} />
          </button>
        )}

        {images.length > 1 && (
          <button
            onClick={nextImage}
            className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-1 text-white"
          >
            <ChevronRight size={14} />
          </button>
        )}
      </div>

      <div className="flex flex-1 flex-col p-1">
        <h3
          className={`line-clamp-2 min-h-[24px] font-bold uppercase leading-3 tracking-wide text-[#0056D2] ${
            priceAmount >= 2000 && priceAmount <= 8000
              ? "text-[8px] md:text-[9px]"
              : "text-[9px]"
          }`}
        >
          {property.title}
        </h3>

        {property.subtitle && (
          <p className="mt-0.5 text-[11px] font-semibold leading-tight text-[#F97316]">
            {property.subtitle}
          </p>
        )}

        <div className="mt-1 flex items-start gap-1 text-gray-500">
          <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-[#0056D2]" />
          <div className="min-w-0 text-[9.5px] leading-tight sm:text-[9px]">
            {cityState && (
              <p className="break-words font-semibold text-gray-600">
                <span className="font-black text-gray-600">Ciudad:</span> {cityState}
              </p>
            )}
            {neighborhoodLabel && (
              <p className="mt-0.5 break-words text-gray-500">
                <span className="font-black text-gray-600">Colonia:</span> {neighborhoodLabel}
              </p>
            )}
            {streetLabel && (
              <p className="mt-0.5 break-words text-gray-500">
                <span className="font-black text-gray-600">Calle:</span> {streetLabel}
              </p>
            )}
          </div>
        </div>

        <div className="text-[10px] font-bold text-[#0F172A]">
          {formatPrice(property)}
          {rentTaxLabel && (
            <span className="font-medium text-gray-500"> {rentTaxLabel}</span>
          )}
        </div>

     
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              if (showAdvisor) {
                runAdvisorAnalysis(event);
                return;
              }
              setShowAdvisor(true);
            }}
            disabled={showAdvisor && (!advisorBusiness.trim() || !coordinates || isAdvisorAnalyzing)}
            className={`flex w-full items-center gap-1 py-1 text-left text-[10px] font-black transition ${
              showAdvisor
                ? "order-2 mt-2 justify-center rounded-md bg-blue-600 px-2 py-1.5 text-white hover:bg-blue-700 disabled:bg-slate-300"
                : "mt-auto text-blue-700 hover:text-blue-900"
            }`}
          >
            {showAdvisor ? (
              <Sparkles size={11} className={isAdvisorAnalyzing ? "animate-spin" : ""} />
            ) : (
              <Brain size={12} />
            )}
            {isAdvisorAnalyzing
              ? "Analizando..."
              : showAdvisor
                ? "Analizar zona + Asesor IA"
                : "Analizador de zona + Asesor IA"}
          </button>

        {showAdvisor && (
  <div
    onClick={(event) => event.stopPropagation()}
    className="order-1 mt-2 border-t border-slate-100 pt-2"
  >
    <p className="text-[9px] leading-4 text-slate-500">
      <b>Zona:</b> revisa competencia y negocios cercanos.{" "}
      <b>Asesor IA:</b> cruza esos datos con el precio y el espacio para
      estimar compatibilidad.
    </p>

    <div className="mt-2 rounded-md border border-blue-100 bg-blue-50 px-2 py-2 text-[9px] leading-4 text-blue-900">
      <p className="font-black">
        Prueba c??mo funciona nuestro Asesor IA en esta propiedad
      </p>
      <p className="mt-0.5">
        Selecciona uno de los giros disponibles. Pr??ximamente podr??s analizar
        cualquier giro de negocio.
      </p>
    </div>

    <label className="mt-2 block text-[9px] font-black text-slate-700">
      Giros disponibles para probar
    </label>
              <select
                value={advisorBusiness}
                onChange={(event) => {
                  setAdvisorBusiness(event.target.value);
                  setAdvisorError("");
                }}
                disabled={
                  isLoadingAdvisorExamples ||
                  savedAdvisorExamples.length === 0
                }
                className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[10px] outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-100"
              >
                <option value="">
                  {isLoadingAdvisorExamples
                    ? "Cargando giros guardados..."
                    : savedAdvisorExamples.length
                      ? "Selecciona un giro guardado"
                      : "Esta propiedad no tiene an??lisis guardados"}
                </option>
                {savedAdvisorExamples.map((example) => (
                  <option key={example.value} value={example.value}>
                    {example.value}
                  </option>
                ))}
              </select>

              {!isLoadingAdvisorExamples &&
                savedAdvisorExamples.length === 0 && (
                  <p className="mt-2 text-[9px] leading-4 text-blue-700">
                    Esta propiedad a??n no tiene an??lisis guardados. Usa el demo
                    de agentes para conocer c??mo funciona.
                  </p>
                )}

              {!coordinates && (
                <p className="mt-2 text-[9px] leading-4 text-orange-700">
                  Esta propiedad necesita ubicaci??n exacta para analizar zona.
                </p>
              )}

              {advisorError && (
                <p className="mt-2 text-[9px] leading-4 text-red-600">
                  {advisorError}
                </p>
              )}

              {advisorResult && (
                <div className="mt-2 space-y-2">
                  <div className={`rounded-md border px-2 py-1.5 ${getAdvisorToneClass(advisorResult.percentage)}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[9px] font-black uppercase">
                        Compatibilidad
                      </span>
                      <span className="text-sm font-black">
                        {advisorResult.percentage}%
                      </span>
                    </div>
                    <p className="mt-0.5 text-[9px] font-bold">
                      {advisorResult.tone}
                    </p>
                  </div>

                  <div className="grid gap-2">
                    {advisorResult.percentage < 55 ? (
                      <div className="rounded-md border border-red-200 bg-red-50 px-2 py-2">
                        <p className="text-[9px] font-black text-red-900">
                          No recomendamos esta propiedad para {analyzedBusiness}.
                        </p>
                        <p className="mt-1 text-[9px] leading-4 text-red-800">
                          Conviene revisar otras opciones con mejor compatibilidad.
                        </p>
                      </div>
                    ) : (
                    <div className="rounded-md border border-emerald-100 bg-white px-2 py-1.5">
                      <p className="text-[8px] font-black uppercase tracking-wide text-emerald-700">
                        Propiedad
                      </p>
                    <p className="mt-1 text-[9px] leading-4 text-slate-600">
                        {advisorResult.reasons?.[0] || "Tiene se??ales ??tiles para evaluarla."}
                      </p>
                    </div>
                    )}

                    <div className="rounded-md border border-blue-100 bg-white px-2 py-1.5">
                      <p className="text-[8px] font-black uppercase tracking-wide text-blue-700">
                        Zona
                      </p>
                      <p className="mt-1 text-[9px] leading-4 text-slate-600">
                        {advisorZoneInsight?.directCompetition
                          ? `${advisorZoneInsight.directCompetition.nearbyTotal ?? advisorZoneInsight.directCompetition.total ?? 0} competidores cerca y ${advisorZoneInsight.otherBusinesses?.total ?? 0} negocios alrededor.`
                          : "Se cruz?? con datos disponibles del entorno."}
                      </p>
                    </div>

                    {advisorResult.risks?.[0] && (
                      <div className="rounded-md border border-amber-100 bg-white px-2 py-1.5">
                        <p className="text-[8px] font-black uppercase tracking-wide text-amber-700">
                          {advisorResult.percentage < 55 ? "Motivo principal" : "Validar"}
                        </p>
                        <p className="mt-1 text-[9px] leading-4 text-slate-600">
                          {advisorResult.risks[0]}
                        </p>
                      </div>
                    )}
                  </div>
                     {analyzedBusiness && (
                    <p className="text-[8px] leading-3 text-slate-400">
                      Resultado final para: {analyzedBusiness}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
  );
};

export default PropertyCard;
